import {translate} from "./Translator";

const minute = 60;
const hour = 60 * minute;
const day = 24 * hour;
const week = 7 * day;
const month = 30 * day;
const year = 365 * day;

/**
 * Store the datetime of time elements in seconds
 * @type {WeakMap<HTMLElement, number>}
 */
const state = new WeakMap();

/** @type {WeakSet<HTMLTimeElement>} */
const relativeElements = new WeakSet();

/** @type {WeakMap<HTMLTimeElement, number>} */
const intervals = new WeakMap();

/**
 * Keys of time elements the user expanded to an absolute datetime, so a swap can restore them
 * @type {Set<string>}
 */
const expandedKeys = new Set();

/**
 * @param {HTMLTimeElement} timeElement
 * @returns {?string}
 */
const memoryKey = timeElement => timeElement.closest('[data-time-key]')?.dataset.timeKey ?? null;

/**
 * @param {HTMLTimeElement} timeElement
 */
const wasExpandedByUser = timeElement => {
    const key = memoryKey(timeElement);

    return key !== null && expandedKeys.has(key);
};

const forgetDetachedKeys = () => {
    expandedKeys.forEach(key => {
        if (document.querySelector(`[data-time-key="${CSS.escape(key)}"]`) === null) {
            expandedKeys.delete(key);
        }
    });
};

/**
 * @param {HTMLTimeElement} timeElement
 */
const rememberToggleState = timeElement => {
    const key = memoryKey(timeElement);

    if (key === null) {
        return;
    }

    forgetDetachedKeys();

    if (relativeElements.has(timeElement)) {
        expandedKeys.delete(key);

        return;
    }

    expandedKeys.add(key);
};

/**
 * @param {HTMLTimeElement} element
 * @returns {number}
 */
const timestampToSeconds = element => Math.trunc(Date.parse(element.getAttribute('datetime')) / 1000);

/**
 * @param {HTMLTimeElement} timeElement
 */
const stopInterval = timeElement => {
    clearInterval(intervals.get(timeElement));
    intervals.delete(timeElement);
};

/**
 * @param {HTMLTimeElement} timeElement
 */
export const timeElementClickedHandler = timeElement => {
    if (!state.has(timeElement)) {
        state.set(timeElement, timestampToSeconds(timeElement));
    }

    if (relativeElements.has(timeElement)) {
        timeElement.textContent = timeElement.dataset.original;
        stopInterval(timeElement);
        relativeElements.delete(timeElement);
    } else {
        fromNow(timeElement);
        intervals.set(timeElement, setInterval(() => fromNow(timeElement), 30 * 1000));
        relativeElements.add(timeElement);
    }

    rememberToggleState(timeElement);
};

/**
 * @param {HTMLTimeElement} timeElement
 */
export const renderRelativeTime = timeElement => {
    if (relativeElements.has(timeElement) || wasExpandedByUser(timeElement)) {
        return;
    }

    timeElementClickedHandler(timeElement);
};

/**
 * @param {ParentNode} root
 */
export const renderRelativeTimesIn = root => {
    root.querySelectorAll?.('time').forEach(renderRelativeTime);
};

/**
 * @param {HTMLElement} element
 */
export const clearRelativeTimeIntervals = element => {
    if (element.tagName === 'TIME') {
        stopInterval(element);
    }

    element.querySelectorAll?.('time').forEach(stopInterval);
};

/**
 * @param {HTMLTimeElement} timeElement
 */
const fromNow = timeElement => {
    const now = Math.trunc(Date.now() / 1000);
    timeElement.textContent = toRelativeTime(state.get(timeElement) - now);
};

/**
 * transforms an integer amount of seconds into a translated relative time string
 * @param {number} seconds - positive for future, negative for past
 */
const toRelativeTime = (seconds) => {
    const direction = seconds >= 0 ? 'future' : 'past';
    seconds = Math.abs(seconds);
    if (seconds >= year) { return pluralizeTranslate(Math.trunc(seconds / year), 'year', direction); }
    if (seconds >= month) { return pluralizeTranslate(Math.trunc(seconds / month), 'month', direction); }
    if (seconds >= week) { return pluralizeTranslate(Math.trunc(seconds / week), 'week', direction); }
    if (seconds >= day) { return pluralizeTranslate(Math.trunc(seconds / day), 'day', direction); }
    if (seconds >= hour) { return pluralizeTranslate(Math.trunc(seconds / hour), 'hour', direction); }
    if (seconds >= minute) { return pluralizeTranslate(Math.trunc(seconds / minute), 'minute', direction); }
    return pluralizeTranslate(1, 'justNow', direction);
};

const pluralizeTranslate = (quantity, period, direction) => {
    const periodKey = quantity > 1 ? `${period}s` : period;
    const translationKey = `component.relativeTime.${direction}.${periodKey}`;

    return translate(translationKey, quantity);
};
